/**
 * Utilities for building Excel export data from eligibility classes
 *
 * Transforms benefit class data into exportable row/column structures
 * for Excel, independent of AgGrid rendering.
 */

import { sortRfpCoverages } from "@platform/types";

import type { RfpCoverage } from "@platform/plan-grid-core";
import type { ExcelRow } from "../rates";

export type Coverage = Pick<RfpCoverage, "id" | "type">;

export interface BenefitClass {
  id: string;
  name: string;
  rfpId: string;
  coverageIds: string[];
  coverages?: Coverage[];
}

const COVERAGE_LABELS: Record<string, string> = {
  dental: "Dental",
  vision: "Vision",
  std: "STD",
  ltd: "LTD",
  voluntaryLifeAdnd: "Voluntary Life",
  eap: "EAP",
  hospitalIndemnity: "HI",
  basicLifeAdnd: "Basic Life",
  criticalIllness: "CI",
  accident: "Accident",
  cancer: "Cancer",
  individualDisabilityInsurance: "IDI",
  wholeLife: "Whole Life",
  fmla: "FMLA",
};

const getCoverageLabel = (type: string): string =>
  COVERAGE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1);

const createCoverageIdSet = (coverageIds: string[]): Set<string> =>
  new Set(coverageIds);

const createHeaderRow = (): ExcelRow => ({
  label: "Classes",
  values: {},
});

const buildRowValues = (
  sortedCoverages: Coverage[],
  valueGetter: (coverage: Coverage) => string,
): Record<string, string> => {
  const values: Record<string, string> = {};
  sortedCoverages.forEach((coverage) => {
    values[coverage.id] = valueGetter(coverage);
  });
  return values;
};

const createColumnHeadersRow = (sortedCoverages: Coverage[]): ExcelRow => ({
  label: "",
  values: buildRowValues(sortedCoverages, (coverage) =>
    getCoverageLabel(coverage.type),
  ),
});

const createClassDataRow = (
  benefitClass: BenefitClass,
  sortedCoverages: Coverage[],
): ExcelRow => {
  const classCoverageIds = createCoverageIdSet(benefitClass.coverageIds);
  return {
    label: benefitClass.name,
    values: buildRowValues(sortedCoverages, (coverage) =>
      classCoverageIds.has(coverage.id) ? "✓" : "",
    ),
  };
};

const createClassDataRows = (
  benefitClasses: BenefitClass[],
  sortedCoverages: Coverage[],
): ExcelRow[] =>
  benefitClasses.map((benefitClass) =>
    createClassDataRow(benefitClass, sortedCoverages),
  );

/**
 * Build Excel rows for eligibility classes
 *
 * Structure:
 * - Header row: "Classes"
 * - Column headers: Class Name | Coverage1 | Coverage2 | ...
 * - Data rows: One per class with checkmarks for assigned coverages
 *
 * @param benefitClasses - Array of benefit classes from API
 * @param coverages - Array of all coverages in the RFP
 * @returns Array of ExcelRow objects
 */
export const buildClassesRows = (
  benefitClasses: BenefitClass[],
  coverages: Coverage[],
): ExcelRow[] => {
  if (benefitClasses.length === 0 || coverages.length === 0) {
    return [];
  }

  const sortedCoverages = sortRfpCoverages(coverages);

  return [
    createHeaderRow(),
    createColumnHeadersRow(sortedCoverages),
    ...createClassDataRows(benefitClasses, sortedCoverages),
  ];
};
